import { Box, CircularProgress } from '@mui/material';
import { fontWeight, Text, textLevel, Icons, colors } from '../../../../../packages';
import { useStyles } from './StepFour.styles';
import { useState, useEffect, useRef } from 'react';
import { btnType, Button, Input, inputSize, inputType } from '../../../../../packages';
import clsx from 'clsx';
import dayjs from '../../../../../utils/dayjs';
import { getPreferredPharmacyDetails } from '../../../../../services/conversation/conversation.service';
import StepThreePharmacy from '../step-three-pharmacy/StepThreePharmacy';
import { MEDICATION_MODE } from '../AddNewReport.constants';

const StepFourMedications = ({
  selectedMedications,
  selectedPharmacy,
  onPharmacySelect,
  memberAddress,
  onGoToMedicationStep,
  isAutomationRunning = false,
  isAutomationLoading = false,
  automationMessages = [],
  automationStatus = null,
  userInputRequest = null,
  onUserInputResponse = () => {},
  isUIFrozen = false,
  isSaving = false,
  securityCodeAttempts,
  setSecurityCodeAttempts,
  mode = 'ADD',
  patientName = '',
  patientId = '',
  zipCode = '',
  memberPreferredPharmacy = null,
}) => {
  const classes = useStyles();
  const [securityCode, setSecurityCode] = useState('');
  const [pharmacyDetails, setPharmacyDetails] = useState(null);
  const [isLoadingPharmacy, setIsLoadingPharmacy] = useState(false);
  // Show pharmacy selection list vs pharmacy card - initially true if no pharmacy
  const [showPharmacySelection, setShowPharmacySelection] = useState(!selectedPharmacy);
  const messagesRef = useRef(null);
  const interactionRef = useRef(null);
  const securityRef = useRef(null);

  // Fetch full pharmacy details when in refill mode
  useEffect(() => {
    const pharmacyId = selectedPharmacy?.id;
    if (!pharmacyId || mode !== 'REFILL') {
      setPharmacyDetails(null);
      return;
    }

    setIsLoadingPharmacy(true);
    getPreferredPharmacyDetails({ pharmacyId })
      .then(({ data }) => setPharmacyDetails(data))
      .catch(() => {})
      .finally(() => setIsLoadingPharmacy(false));
  }, [selectedPharmacy?.id, mode]);

  const displayPharmacy = mode === 'REFILL' ? pharmacyDetails : selectedPharmacy;

  // Auto-scroll to bottom when this box appears
  useEffect(() => {
    if (interactionRef.current) {
      // Smooth scroll to bottom
      interactionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    } else if (securityRef.current) {
      // Smooth scroll to bottom
      securityRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    } else if (messagesRef.current && automationMessages.length > 0) {
      // Smooth scroll to bottom
      messagesRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [interactionRef, messagesRef, automationMessages, securityRef]);

  // Reset security code attempts when new security code request comes in
  useEffect(() => {
    if (userInputRequest?.type === 'security_code_request') {
      setSecurityCodeAttempts(prev => prev + 1);
    }
  }, [userInputRequest]);

  // Icon box component for consistent styling
  const IconBox = ({ glyph }) => (
    <Box
      sx={{
        width: '55px',
        height: '55px',
        borderRadius: '8px',
        backgroundColor: colors.primary50,
        border: `1px solid ${colors.primary300}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icons glyph={glyph} color={colors.primary500} width="32px" height="32px" />
    </Box>
  );

  const renderPatientInfoCard = () => {
    return (
      <Box className={classes.infoCard}>
        <Box display="flex" gap={2} alignItems="center">
          <IconBox glyph="member" />
          <Box className={classes.cardContent}>
            <Text weight={fontWeight.SEMI_BOLD} level={textLevel.M}>
              {patientName} {patientId ? `- ${patientId}` : ''}
            </Text>
            {memberAddress?.line1 && (
              <Text level={textLevel.S} color={colors.neutral500}>
                {memberAddress.line1}
              </Text>
            )}
            {memberAddress?.line2 && (
              <Text level={textLevel.S} color={colors.neutral500}>
                {memberAddress.line2}
              </Text>
            )}
          </Box>
          {/* </Box> */}
        </Box>
      </Box>
    );
  };

  const renderMedicationDetailsCard = () => {
    const medication = selectedMedications[0];
    if (!medication) return null;

    const sig = medication?.sig || '';
    const medicationName = medication?.externalRx?.label || medication?.item?.label || '';
    const showChangeOption =
      (mode === 'UPDATE' || mode === 'ADD') &&
      !isSaving &&
      !isUIFrozen &&
      !isAutomationRunning &&
      !isAutomationLoading;

    return (
      <Box className={classes.infoCard}>
        <Box display="flex" gap={2} alignItems="center">
          <IconBox glyph="pharmacy" />
          <Box display="flex" flexDirection="column" gap="4px" flex={1}>
            <Box className={classes.cardHeader}>
              <Text weight={fontWeight.SEMI_BOLD} level={textLevel.M}>
                {medicationName}
              </Text>
              {showChangeOption && (
                <Text className={classes.cardAction} onClick={onGoToMedicationStep}>
                  Change
                </Text>
              )}
            </Box>
            <Text level={textLevel.S} color={colors.neutral500}>
              {sig}
            </Text>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderPharmacyCard = () => {
    const pharmacyName = displayPharmacy?.name;

    return (
      <Box className={classes.infoCard}>
        <Box display="flex" gap={2} alignItems="center">
          <IconBox glyph="medication-2" />
          <Box display="flex" flexDirection="column" gap="4px" flex={1}>
            <Box className={classes.cardHeader}>
              <Text weight={fontWeight.SEMI_BOLD} level={textLevel.M}>
                {pharmacyName}
              </Text>
              {!isSaving && !isUIFrozen && !isAutomationRunning && !isAutomationLoading && (
                <Text className={classes.cardAction} onClick={() => setShowPharmacySelection(true)}>
                  Change
                </Text>
              )}
            </Box>
            <Box className={classes.cardContent}>
              {isLoadingPharmacy ? (
                <Text className={classes.pharmacyAddress}>Loading...</Text>
              ) : displayPharmacy ? (
                <>
                  {displayPharmacy.address2 && (
                    <Text className={classes.pharmacyAddress}>{displayPharmacy.address2}</Text>
                  )}
                  {displayPharmacy.address1 && (
                    <Text className={classes.pharmacyAddress}>{displayPharmacy.address1}</Text>
                  )}
                </>
              ) : (
                <Text className={classes.pharmacyAddress}>No pharmacy selected</Text>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const handlePharmacySelect = pharmacy => {
    onPharmacySelect(pharmacy);
    // Only switch to card view when a pharmacy is actually selected
    if (pharmacy) {
      setShowPharmacySelection(false);
    }
  };

  const renderPharmacySelection = () => {
    return (
      <Box paddingY={2}>
        <StepThreePharmacy
          selectedPharmacy={selectedPharmacy}
          onPharmacySelect={handlePharmacySelect}
          zipCode={zipCode}
          memberPreferredPharmacy={memberPreferredPharmacy}
          disableAutoSelect={true}
        />
      </Box>
    );
  };

  const renderAutomationLoader = () => {
    const getStatusText = status => {
      switch (status) {
        case 'success':
          return 'Success';
        case 'error':
          return 'Error';
        case 'in_progress':
          return 'In Progress';
        case 'starting':
          return 'Submitting';
        default:
          return 'Submitting';
      }
    };

    return (
      <Box className={classes.card} style={{ border: '2px solid #1976d2' }}>
        <Box className={classes.cardHeader}>
          <Text weight={fontWeight.BOLD} className={classes.cardTitle}>
            {automationStatus?.status === 'error' && 'Error '}Submitting Medication Data
          </Text>
          {automationStatus?.status && automationStatus?.status !== 'error' && (
            <CircularProgress color="secondary" className={classes.progressIcon} />
          )}
        </Box>
        <Box className={classes.cardContent}>
          {/* Show current status */}
          {automationStatus && (
            <Box display="flex" flexDirection="column" gap={1} mb={1}>
              <Box>
                <Text level={textLevel.M} weight={fontWeight.BOLD}>
                  Current Status: {getStatusText(automationStatus.status)}
                </Text>
              </Box>
              {automationStatus?.status !== 'error' && (
                <Box>
                  <Text level={textLevel.S}>Step: {automationStatus.message}</Text>
                </Box>
              )}
            </Box>
          )}

          {/* Show automation steps as checklist */}
          {automationMessages.length > 0 && (
            <Box ref={messagesRef}>
              <Text weight={fontWeight.BOLD} level={textLevel.S}>
                Progress Logs:
              </Text>
              {automationMessages.map((msg, index) => (
                <Box key={index} display="flex" alignItems="center" gap={1} mb={1} mt={1}>
                  <Icons
                    glyph={
                      msg.type === 'error'
                        ? 'clear-circle'
                        : msg.type === 'success'
                        ? 'check-circle'
                        : msg.type === 'alert'
                        ? 'alert-filled'
                        : 'info'
                    }
                    color={
                      msg.type === 'error'
                        ? colors.rose500
                        : msg.type === 'success'
                        ? colors.green500
                        : msg.type === 'alert'
                        ? colors.warning500
                        : colors.primary500
                    }
                    className={classes.logIcon}
                  />
                  <Box display="flex" justifyContent="space-between" width="100%">
                    <Text level={textLevel.S}>{msg.message}</Text>
                    <Text className={classes.subText}>{dayjs(msg.timestamp).format('hh:mm a')}</Text>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const renderAutomationApiLoading = () => (
    <Box className={classes.card} style={{ border: '2px solid #1976d2' }}>
      <Box className={classes.cardHeader}>
        <Text weight={fontWeight.BOLD} className={classes.cardTitle}>
          Processing Submission
        </Text>
        <CircularProgress color="secondary" className={classes.progressIcon} />
      </Box>
      <Box className={classes.cardContent}>
        <Text level={textLevel.M}>Initiating submission. This may take a few moments.</Text>
      </Box>
    </Box>
  );

  const renderUserInputRequest = () => {
    if (!userInputRequest) return null;

    switch (userInputRequest.type) {
      case 'drug_conflict_alert':
        return (
          <Box
            ref={interactionRef}
            className={classes.card}
            style={{ border: `1px solid ${colors.secondary500}`, backgroundColor: colors.secondary50 }}
          >
            <Box className={classes.cardHeader}>
              <Text weight={fontWeight.BOLD} className={classes.cardTitle}>
                {userInputRequest.alertCount} Alert(s) -{' '}
                {userInputRequest.alerts[0]?.alertType || 'Drug Interaction'}
              </Text>
              <Box display="flex" gap={2}>
                <Text
                  className={clsx(classes.cardAction, classes.warningText)}
                  onClick={() => onUserInputResponse('cancel')}
                >
                  Change RX
                </Text>
                <Text className={classes.cardAction} onClick={() => onUserInputResponse('override')}>
                  Force RX
                </Text>
              </Box>
            </Box>
            <Box className={classes.cardContent}>
              {/* Display multiple alerts */}
              {userInputRequest.alerts &&
                userInputRequest.alerts.map((alert, index) => (
                  <Box key={index}>
                    <Box display="flex" flexDirection="column">
                      <Text level={textLevel.S} weight={fontWeight.BOLD}>
                        {alert.title}
                      </Text>
                      <Text level={textLevel.S}>{alert.description}</Text>
                    </Box>

                    {/* Add divider between alerts (except for last one) */}
                    {index < userInputRequest.alerts.length - 1 && (
                      <Box
                        style={{
                          height: '1px',
                          backgroundColor: '#e0e0e0',
                          margin: '16px 0',
                          opacity: 0.5,
                        }}
                      />
                    )}
                  </Box>
                ))}
            </Box>
          </Box>
        );

      case 'security_code_request':
        // Check if max attempts reached
        if (securityCodeAttempts > 4) {
          // Auto-send 'error' string to backend on 3rd attempt
          onUserInputResponse('error');
        }

        return (
          <Box
            ref={securityRef}
            className={classes.card}
            style={{
              border: `1px solid ${securityCodeAttempts > 1 ? colors.rose500 : colors.success500}`,
              backgroundColor: securityCodeAttempts > 1 ? colors.rose50 : colors.success50,
            }}
          >
            <Box className={classes.cardHeader}>
              <Text weight={fontWeight.BOLD} className={classes.cardTitle}>
                Verify Controlled Substance RX
              </Text>
              {securityCodeAttempts > 1 && (
                <Text level={textLevel.S}>Attempt {securityCodeAttempts - 1} of 3</Text>
              )}
            </Box>
            <Box className={classes.cardContent}>
              <Text level={textLevel.S} weight={fontWeight.SEMI_BOLD}>
                Security code sent to {userInputRequest.data?.phone || 'your phone'}
              </Text>
              <Input
                fullWidth
                variant={inputType.DEFAULT}
                size={inputSize.M}
                placeholder="Enter security code"
                sx={{ backgroundColor: colors.white }}
                onChange={e => setSecurityCode(e.target.value)}
                value={securityCode}
              />
              {securityCodeAttempts > 1 && (
                <Text level={textLevel.S} weight={fontWeight.BOLD} className={classes.alertText}>
                  Incorrect Security Code. Check the code closely.
                </Text>
              )}
              <Button
                className={classes.alertBtn}
                variant={btnType.PRIMARY}
                onClick={() => {
                  if (securityCode.trim()) {
                    onUserInputResponse(securityCode);
                    setSecurityCode(''); // Clear input after submission
                  }
                }}
                disabled={!securityCode.trim()}
              >
                Submit Code
              </Button>
            </Box>
          </Box>
        );

      default:
        // Handle generic alerts with title and description
        return (
          <Box className={classes.card}>
            <Box className={classes.cardHeader}>
              <Text weight={fontWeight.BOLD} className={classes.cardTitle}>
                {userInputRequest.title || 'Alert'}
              </Text>
            </Box>
            <Box className={classes.cardContent}>
              <Text style={{ marginBottom: '16px' }}>
                {userInputRequest.description || 'An alert requires your attention.'}
              </Text>
              <Box display="flex" gap={2}>
                <Button
                  className={classes.alertBtn}
                  variant={btnType.PRIMARY}
                  onClick={() => onUserInputResponse('acknowledge')}
                >
                  Acknowledge
                </Button>
              </Box>
            </Box>
          </Box>
        );
    }
  };

  return (
    <Box
      className={clsx(classes.container, {
        [classes.contentRefill]: mode === MEDICATION_MODE.REFILL || mode === MEDICATION_MODE.UPDATE,
      })}
    >
      {renderPatientInfoCard()}
      {renderMedicationDetailsCard()}

      {/* Show pharmacy selection or pharmacy card */}
      {showPharmacySelection ? renderPharmacySelection() : renderPharmacyCard()}

      {/* Show automation loader when running */}
      {isAutomationLoading && renderAutomationApiLoading()}
      {(isAutomationRunning || automationMessages.length > 0 || automationStatus) && renderAutomationLoader()}

      {/* Show user input requests */}
      {renderUserInputRequest()}
    </Box>
  );
};

export default StepFourMedications;
